import {
    Component,
    OnInit
} from '@angular/core';

import { TableauService } from '../../services/tableau.service';

let tableauScriptPromise: Promise<void> | null = null;

function loadTableauScript(vizUrl: string): Promise<void> {
    if (customElements.get('tableau-viz')) {
        return Promise.resolve();
    }

    if (tableauScriptPromise) {
        return tableauScriptPromise;
    }

    const tableauOrigin = new URL(vizUrl).origin;

    const scriptUrl =
        `${tableauOrigin}/javascripts/api/` +
        'tableau.embedding.3.latest.min.js';

    tableauScriptPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector(
            'script[data-tableau-embedding="true"]'
        ) as HTMLScriptElement | null;

        if (existingScript) {
            existingScript.addEventListener(
                'load',
                () => resolve(),
                { once: true }
            );

            existingScript.addEventListener(
                'error',
                () => reject(
                    new Error('Unable to load Tableau Embedding API.')
                ),
                { once: true }
            );

            return;
        }

        const script = document.createElement('script');

        script.type = 'module';
        script.src = scriptUrl;
        script.dataset['tableauEmbedding'] = 'true';

        script.onload = () => {
            if (customElements.get('tableau-viz')) {
                resolve();
            } else {
                reject(
                    new Error('Tableau visualization element was not registered.')
                );
            }
        };

        script.onerror = () => {
            reject(new Error('Unable to load Tableau Embedding API.'));
        };

        document.head.appendChild(script);
    }).catch(error => {
        tableauScriptPromise = null;
        throw error;
    });

    return tableauScriptPromise;
}

@Component({
    selector: 'app-netsuite-ledger',
    standalone: false,
    templateUrl: './netsuite-ledger.component.html',
    styleUrls: ['./netsuite-ledger.component.css']
})
export class NetsuiteLedgerComponent implements OnInit {

    token = '';
    vizUrl = '';

    loading = true;
    errorMessage = '';

    constructor(
        private readonly tableauService: TableauService
    ) { }

    ngOnInit(): void {
        this.loadReport();
    }

    loadReport(): void {
        this.loading = true;
        this.errorMessage = '';

        this.tableauService.getEmbedToken().subscribe({
            next: response => {
                if (!response?.token || !response?.vizUrl) {
                    this.showError(
                        'The server returned an incomplete Tableau response.'
                    );
                    return;
                }

                this.token = response.token;
                this.vizUrl = response.vizUrl;

                loadTableauScript(this.vizUrl)
                    .then(() => {
                        this.loading = false;
                    })
                    .catch(error => {
                        console.error(
                            'Tableau Embedding API failed to load.',
                            error
                        );

                        this.showError(
                            'Unable to load the Tableau report. Please try again.'
                        );
                    });
            },

            error: error => {
                console.error(
                    'Unable to retrieve the Tableau embed token.',
                    error
                );

                if (error.status === 401) {
                    this.showError(
                        'Your session has expired. Please sign in again.'
                    );
                } else if (error.status === 403) {
                    this.showError(
                        'You do not have permission to access this report.'
                    );
                } else {
                    this.showError(
                        'Unable to load the Tableau report. Please try again later.'
                    );
                }
            }
        });
    }

    onVizLoadError(event: Event): void {
        console.error('Tableau load error:', event);

        this.showError(
            'Cannot access data. Please contact support.'
        );
    }

    private showError(message: string): void {
        this.loading = false;
        this.errorMessage = message;
    }

    retry(): void {
        this.loadReport();
    }
}